import React, { useState, useEffect } from "react";

function WeightConverter() {
  const [inputValue, setInputValue] = useState("");
  const [outputValue, setOutputValue] = useState("");
  const [conversionDirection, setConversionDirection] = useState("kgToLbs");
  const [error, setError] = useState("");

  // Conversion factors
  const KG_TO_LBS = 2.20462;
  const LBS_TO_KG = 0.453592;

  // Weight limits
  const KG_MIN = 2;
  const KG_MAX = 700;
  const LBS_MIN = 4;
  const LBS_MAX = 1550;

  // Validate input - numbers only, up to one decimal place, and within limits
  const validateInput = (value) => {
    if (value === "") {
      setError("");
      return true;
    }

    // Check if it's only digits and decimal
    const basicRegex = /^\d*\.?\d*$/;
    if (!basicRegex.test(value)) {
      setError("Please enter a valid number");
      return false;
    }

    // Check decimal places
    if (value.includes(".")) {
      const [, decimals] = value.split(".");
      if (decimals.length > 1) {
        setError("You can only enter up to one decimal place");
        return false;
      }
    }

    // Check if value is within limits
    const numericValue = parseFloat(value);
    if (conversionDirection === "kgToLbs") {
      if (numericValue < KG_MIN) {
        setError(`Weight must be at least ${KG_MIN} kg`);
        return false;
      } else if (numericValue > KG_MAX) {
        setError(`Weight must be less than ${KG_MAX} kg`);
        return false;
      }
    } else {
      if (numericValue < LBS_MIN) {
        setError(`Weight must be at least ${LBS_MIN} lbs`);
        return false;
      } else if (numericValue > LBS_MAX) {
        setError(`Weight must be less than ${LBS_MAX} lbs`);
        return false;
      }
    }

    setError("");
    return true;
  };

  // Handle input change with validation
  const handleInputChange = (e) => {
    const value = e.target.value;
    if (validateInput(value)) {
      setInputValue(value);
    }
  };

  // Perform conversion based on direction
  const performConversion = () => {
    if (!inputValue || inputValue === "" || error) {
      setOutputValue("");
      return;
    }

    const numericValue = parseFloat(inputValue);
    if (isNaN(numericValue)) {
      setOutputValue("");
      return;
    }

    // Check limits again (in case direction changed)
    if (conversionDirection === "kgToLbs") {
      if (numericValue < KG_MIN || numericValue > KG_MAX) {
        setOutputValue("");
        return;
      }
    } else {
      if (numericValue < LBS_MIN || numericValue > LBS_MAX) {
        setOutputValue("");
        return;
      }
    }

    let result;
    if (conversionDirection === "kgToLbs") {
      result = (numericValue * KG_TO_LBS).toFixed(1);
      setOutputValue(`${inputValue} kg = ${result} lbs`);
    } else {
      result = (numericValue * LBS_TO_KG).toFixed(1);
      setOutputValue(`${inputValue} lbs = ${result} kg`);
    }
  };

  // Toggle conversion direction
  const toggleConversionDirection = () => {
    setConversionDirection(
      conversionDirection === "kgToLbs" ? "lbsToKg" : "kgToLbs"
    );
    // Clear values when switching
    setInputValue("");
    setOutputValue("");
    setError("");
  };

  // Perform conversion when input or direction changes
  useEffect(() => {
    performConversion();
  }, [inputValue, conversionDirection]);

  return (
    <>
      <div id="kg_to_lb_calc_tab" role="tabpanel">
        <div className="row mt-2">
          <div className="col-md-6">
            <h3>Kilogram</h3>
            <p style={{ fontSize: 16, textAlign: "justify" }}>
              <strong>Definition</strong>: A kilogram (symbol: kg) is the base
              unit of mass in the International System of Units (SI).
            </p>
            <div className="clearfix">
              <br />
            </div>
            <h3>Pound</h3>
            <p style={{ fontSize: 16, textAlign: "justify" }}>
              <strong>Definition</strong>: A pound (symbol: lb) is a unit of
              mass used in the imperial and US customary systems of measurement.
            </p>
          </div>
          <div className="col-md-6">
            <div style={{ width: "100%" }}>
              <div className="body-fat-ratio-heading d-flex justify-content-center align-items-center">
                <h5 className="text-center">Weight Converter</h5>
              </div>

              <div style={{ backgroundColor: "#F7F7F7" }}>
                <div
                  className="btn-group pt-4"
                  role="group"
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <button
                    type="button"
                    className={`chart-views-btn ${
                      conversionDirection === "kgToLbs"
                        ? "chart-active-views-btn"
                        : "chart-views-btn"
                    }`}
                    onClick={() => setConversionDirection("kgToLbs")}
                  >
                    kg → lbs
                  </button>
                  <button
                    type="button"
                    className={`chart-views-btn-second ${
                      conversionDirection === "lbsToKg"
                        ? "chart-active-views-btn"
                        : "chart-views-btn-second"
                    }`}
                    onClick={() => setConversionDirection("lbsToKg")}
                  >
                    lbs → kg
                  </button>
                </div>
                <div className="clearfix">
                  <br />
                </div>
                <form
                  style={{ width: "95%", marginLeft: "8px", marginTop: "5px" }}
                >
                  <div className="row" style={{ borderBottom: "0px" }}>
                    <div className="col-lg-12">
                      <label
                        className="control-label mb-10"
                        htmlFor="weightInput"
                      >
                        {conversionDirection === "kgToLbs"
                          ? "Kilogram"
                          : "Pound"}
                      </label>
                      <input
                        className={`ucdcinput form-control mb-1 ${
                          error ? "is-invalid" : ""
                        }`}
                        id="weightInput"
                        value={inputValue}
                        onChange={handleInputChange}
                        type="text"
                        placeholder={
                          conversionDirection === "kgToLbs"
                            ? "2-700"
                            : "4-1550"
                        }
                      />
                      {error && (
                        <div className="text-danger small mt-0">{error}</div>
                      )}
                    </div>
                    <h5 className="text-center my-3">
                      <strong>To</strong>
                    </h5>
                    <div className="col-lg-12">
                      <label
                        className="control-label mb-10"
                        htmlFor="weightOutput"
                      >
                        {conversionDirection === "kgToLbs"
                          ? "Pound"
                          : "Kilogram"}
                      </label>
                      <input
                        className="ucdcinput form-control"
                        id="weightOutput"
                        value={
                          outputValue.split("=")[1]
                            ? outputValue.split("=")[1].trim()
                            : ""
                        }
                        type="text"
                        readOnly
                      />
                    </div>
                    <div className="col-lg-12 mb-3">
                      <div
                        id="ucresult"
                        className="ucresulttext text-center mt-3"
                      >
                        {outputValue && (
                          <div className="alert alert-info mb-0 py-2">
                            <strong>Result:</strong> {outputValue}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default WeightConverter;